import { Header } from 'layout';
import { withPlaceholder } from 'HOC';
import { useIraUserForm } from 'hooks/forms';
import { useGetIraUser, useEditIraUser } from 'hooks/api/iraUser';
import { useUnsaveChangesModal } from 'hooks/modals';
import { useGetProfileList } from 'hooks/api/profiles';
import { useGetProfile } from 'hooks/api/login';
import { useGetDefaultUserManagers } from 'hooks/api/userManager';
import { useGetEmployer } from 'hooks/api/employer';
import {
  formatInputDateForBE,
  parseInputDate,
  getDirtyFieldValues,
  mapCustodianTypeValueToKey,
} from 'utils';
import { USER_TYPE } from 'constants/userTypes';
import { mapIndependentIraTypeValueToKey } from 'screens/IndependentIra/IraIndependentIra.utils';
import Button from 'components/Button';
import { EntityStatus } from 'components/Label';
import StatusUserButton from 'components/StatusUserButton';
import SaveDiscardingChangesModal from 'components/UnsaveChangesModal';
import IraUserForm from '../components/IraUserForm';
import IraUserNavTabs from '../components/IraUserNavTabs';

import messages from './IraUserInfo.messages';
import styles from './IraUserInfo.module.css';

const IraUserFormWithPlaceholders = withPlaceholder(IraUserForm);

const IraUserInfo = (): JSX.Element => {
  const {
    data: iraUser,
    isLoading: isIraUserLoading,
    isError: isIraUserError,
    refetch,
    isRefetching,
  } = useGetIraUser();
  const { mutateAsync: editIraUser, isLoading: isLoadingEdit } = useEditIraUser();
  const { data: otherAccounts } = useGetProfileList(iraUser?.email);
  const { data: profile } = useGetProfile();
  const { defaultEtzFee, defaultCustodyFeePercentageIraClub } = profile || {};
  const { data: defaultUserManagersData } = useGetDefaultUserManagers();
  const { data: employerData } = useGetEmployer(iraUser?.employerId);

  const form = useIraUserForm(iraUser);
  const { handleSubmit, getValues, formState } = form;
  const { isDirty, dirtyFields } = formState;

  const onEditIraUserClick = async (): Promise<void> => {
    const values = getValues();
    const dirtyFieldValues = getDirtyFieldValues(dirtyFields, {
      ...values,
      custodianType: mapCustodianTypeValueToKey(values.custodianType) ?? '',
      profileType: mapIndependentIraTypeValueToKey(values.profileType) ?? '',
      custodyFeeAbsolute: values.custodyFeeAbsolute ?? '',
      custodyFeePercentage: values.custodyFeePercentage ?? '',
      nextBillingDate: values.nextBillingDate
        ? formatInputDateForBE(parseInputDate(values.nextBillingDate))
        : '',
    });
    await editIraUser(dirtyFieldValues);
  };

  const unsaveChangesModal = useUnsaveChangesModal(
    form,
    form.reset.bind(null, {
      ...iraUser,
      custodyFeeAbsolute: !!iraUser?.custodyFeeAbsolute
        ? String(iraUser.custodyFeeAbsolute)
        : undefined,
      custodyFeePercentage: !!iraUser?.custodyFeePercentage
        ? String(iraUser.custodyFeePercentage)
        : undefined,
    }),
  );

  const onSubmit = handleSubmit(onEditIraUserClick);

  return (
    <>
      <Header
        title={
          iraUser
            ? `${iraUser?.firstName} ${iraUser?.middleName ?? ''} ${iraUser?.lastName}`
            : ''
        }
        complexComponent={true}
        label={{
          status: iraUser?.profileStatus ?? '',
          entityStatus: EntityStatus.User,
          isAccountClosed: iraUser?.accountClosed,
        }}
        showArrow={true}
      >
        {iraUser && isDirty && (
          <>
            <Button
              className={styles.iraUserInfo__cancel}
              variant={'outline'}
              onClick={unsaveChangesModal.onCancelClick}
            >
              {messages.cancel}
            </Button>
            <Button
              className={styles.iraUserInfo__save}
              onClick={onSubmit}
              isLoading={isLoadingEdit}
            >
              {messages.save}
            </Button>
          </>
        )}

        {iraUser && !isDirty && (
          <StatusUserButton user={iraUser} userType={USER_TYPE.iraUser} />
        )}
      </Header>

      <IraUserNavTabs />
      <SaveDiscardingChangesModal {...unsaveChangesModal} />

      <IraUserFormWithPlaceholders
        form={form}
        isLoading={isIraUserLoading}
        isError={isIraUserError}
        profileStatus={iraUser?.profileStatus ?? ''}
        lastActivity={iraUser?.lastLogin}
        accountNumber={iraUser?.accountNumber}
        isLocked={!!iraUser?.isLocked}
        refetch={refetch}
        isRefetching={isRefetching}
        {...iraUser}
        otherAccounts={otherAccounts}
        email={iraUser?.email}
        defaultEtzFee={defaultEtzFee}
        defaultCustodyFeePercentageIraClub={defaultCustodyFeePercentageIraClub}
        defaultUserManager={defaultUserManagersData}
        employerName={employerData?.name}
      />
    </>
  );
};

export default IraUserInfo;
